import { clientActions } from "store/client-slice";
import {
  checkUserHasManagerRole,
  checkUserHasClientRole,
  checkUserHasMemberRole,
  readSessionCurrentManager,
  readSessionCurrentClient,
  getMemberClients,
} from "app/services/app.state.service";
import { loadMembersFromBackendForManager } from "app/services/manager.service";
import { loadAllActivityTypesByClient } from "app/services/activity.stats.service";

// Resolve the clients the logged-in user works with, plus their manager_id per
// client (managers only — needed for the manager-scoped members endpoint).
// Returns [{ clientId, managerId }]. Admins are skipped: they can span many
// clients, so their reference data is loaded on demand instead of up front.
const resolvePrefetchTargets = async () => {
  if (checkUserHasManagerRole()) {
    const list = readSessionCurrentManager();
    return (Array.isArray(list) ? list : [])
      .filter((c) => c?.clientId != null)
      .map((c) => ({ clientId: c.clientId, managerId: c.id }));
  }
  if (checkUserHasClientRole()) {
    const client = readSessionCurrentClient();
    return client?.id != null ? [{ clientId: client.id, managerId: null }] : [];
  }
  if (checkUserHasMemberRole()) {
    const list = await getMemberClients();
    return (Array.isArray(list) ? list : [])
      .filter((m) => m?.client?.id != null)
      .map((m) => ({ clientId: m.client.id, managerId: null }));
  }
  return [];
};

/**
 * Warm the per-client reference caches (activity types, and a manager's members)
 * for the consolidated report and the Add Activity / Add Leave dialogs, so their
 * dropdowns stop re-fetching on every change. Fire-and-forget: each request is
 * independent and failures are swallowed so a slow/failed call never blocks the
 * dashboard. Returns a promise that resolves once every warm-up has settled
 * (used by tests; callers normally don't await it).
 */
export const prefetchReportReferenceData = async (dispatch) => {
  const targets = await resolvePrefetchTargets().catch(() => []);

  await Promise.all(
    targets.map(({ clientId, managerId }) => {
      const jobs = [
        loadAllActivityTypesByClient(clientId)
          .then((r) =>
            dispatch(clientActions.cacheTypesForClient({ clientId, data: r?.data || [] }))
          )
          .catch(() => {}),
      ];

      if (managerId != null) {
        jobs.push(
          loadMembersFromBackendForManager(managerId)
            .then((r) => {
              const members = r?.status === 204 ? [] : r?.data || [];
              // Warm both caches from one fetch: report filter reads by client id, dialogs by manager id.
              dispatch(clientActions.cacheMembersForClient({ clientId, data: members }));
              dispatch(clientActions.cacheMembersForManager({ managerId, data: members }));
            })
            .catch(() => {})
        );
      }

      return Promise.all(jobs);
    })
  );
};
